import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Input,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  notify,
} from '@aeris/ui'

import {
  ApiError,
  fetchPlatformMaps,
  savePlatformMaps,
  type PlatformMapsView,
} from '../lib/api.ts'

/**
 * O mapa do Aeris, na Plataforma (F265; o fornecedor, F343).
 *
 * As rotas de entrega usam UMA chave, do Aeris: endereço vira ponto, tempos
 * entre as paradas e o caminho. O fornecedor é o Google (pago) ou o
 * OpenRouteService (grátis, só e-mail). A chave entra aqui e nunca volta. O
 * teto é por conta e por mês, somadas as buscas.
 */

const PROVIDER_LABEL = {
  google: 'Google',
  openroute: 'OpenRouteService (grátis)',
} as const

type Provider = keyof typeof PROVIDER_LABEL

const message = (failure: unknown): string =>
  failure instanceof ApiError
    ? failure.message
    : 'Não foi possível concluir. Tente de novo.'

const Form = ({ data }: { data: PlatformMapsView }): ReactNode => {
  const queryClient = useQueryClient()
  const [provider, setProvider] = useState<Provider>(data.provider)
  const [apiKey, setApiKey] = useState('')
  const [cap, setCap] = useState(String(data.defaultMonthlyCallCap))
  const save = useMutation({
    mutationFn: async () =>
      await savePlatformMaps({
        provider,
        ...(apiKey.trim() === ''
          ? {}
          : provider === 'openroute'
            ? { openRouteKey: apiKey.trim() }
            : { googleKey: apiKey.trim() }),
        defaultMonthlyCallCap: Number(cap),
        version: data.version,
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData(['platform-maps'], saved)
      setApiKey('')
      notify.success('O mapa foi salvo.')
    },
  })

  const hasKey =
    provider === 'openroute' ? data.hasOpenRouteKey : data.hasGoogleKey
  const name = provider === 'openroute' ? 'OpenRouteService' : 'Google'

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <Field label="Fornecedor do mapa" className="w-72">
        {(field) => (
          <Select
            {...field}
            value={provider}
            onChange={(event) => {
              setProvider(event.target.value as Provider)
              setApiKey('')
            }}
          >
            {(Object.keys(PROVIDER_LABEL) as Provider[]).map((option) => (
              <option key={option} value={option}>
                {PROVIDER_LABEL[option]}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <p className="text-(length:--text-sm)">
        {hasKey
          ? `A chave do ${name} está gravada.`
          : 'Ainda não há chave: as lojas com Rotas de entrega veem o aviso.'}
      </p>
      <Field
        label={hasKey ? `Trocar a chave do ${name}` : `Chave do ${name}`}
        hint={
          provider === 'openroute'
            ? 'Crie a conta em openrouteservice.org (só e-mail) e copie a chave no painel (Dashboard).'
            : 'Geocoding API, Places API (New) e Routes API ligadas no Google Cloud.'
        }
      >
        {(field) => (
          <Input
            {...field}
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(event) => {
              setApiKey(event.target.value)
            }}
          />
        )}
      </Field>
      <Field label="Buscas por conta, por mês" className="w-56">
        {(field) => (
          <Input
            {...field}
            inputMode="numeric"
            value={cap}
            onChange={(event) => {
              setCap(event.target.value.replace(/\D/g, ''))
            }}
          />
        )}
      </Field>
      {save.isError ? (
        <InlineAlert tone="danger">{message(save.error)}</InlineAlert>
      ) : null}
      <div>
        <Button type="submit" disabled={save.isPending || Number(cap) < 1}>
          {save.isPending ? 'Salvando…' : 'Salvar'}
        </Button>
      </div>
    </form>
  )
}

export const PlatformMaps = ({
  onClose,
}: {
  readonly onClose: () => void
}): ReactNode => {
  const current = useQuery({
    queryKey: ['platform-maps'],
    queryFn: async ({ signal }) => await fetchPlatformMaps(signal),
  })
  const data = current.data

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title="Mapa"
        description="O fornecedor e a chave que as Rotas de entrega de todas as contas usam, o teto de buscas por conta e o uso do mês."
        className="max-w-2xl"
      >
        {current.isError ? (
          <InlineAlert tone="danger">{message(current.error)}</InlineAlert>
        ) : data === undefined ? null : (
          <div className="flex flex-col gap-5">
            <Form data={data} />
            <div className="flex flex-col gap-2">
              <p className="text-(length:--text-sm) font-medium">
                Uso em {data.month.split('-').reverse().join('/')}
              </p>
              {data.usage.length === 0 ? (
                <p className="text-(length:--text-sm) text-(--text-muted)">
                  Nenhuma conta usou o mapa neste mês.
                </p>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>Conta</TableHeaderCell>
                        <TableHeaderCell className="text-right">
                          Endereços
                        </TableHeaderCell>
                        <TableHeaderCell className="text-right">
                          Pelo nome
                        </TableHeaderCell>
                        <TableHeaderCell className="text-right">
                          Tempos
                        </TableHeaderCell>
                        <TableHeaderCell className="text-right">
                          Caminhos
                        </TableHeaderCell>
                        <TableHeaderCell className="text-right">
                          Total
                        </TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.usage.map((row) => (
                        <TableRow key={row.tenantId}>
                          <TableCell>{row.tenantName}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {row.geocode}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {row.place}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {row.matrix}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {row.route}
                          </TableCell>
                          <TableCell className="text-right font-medium tabular-nums">
                            {row.calls} / {data.defaultMonthlyCallCap}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
